import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Select from 'react-select';
import { useAuth } from '../context/AuthContext';
import { getProfile, updateProfile, getCountries, Country, uploadAvatar, changePassword } from '../services/api';
import { Creator } from '../services/api';
import Navbar from '../components/Navbar';
import Button from '../components/Button';
import Input from '../components/Input';
import { t } from '../i18n';
import './ProfilePage.css';

const ProfilePage: React.FC = () => {
  const { creator, refreshProfileStatus } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectMessage = (location.state as any)?.message;
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [profile, setProfile] = useState<Creator | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [selectedAvatarFile, setSelectedAvatarFile] = useState<File | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [infoMessage, setInfoMessage] = useState(redirectMessage || '');
  
  // Form fields
  const [formData, setFormData] = useState({
    name: '',
    alias: '',
    phone: '',
    avatar_url: '',
    country: '',
    description: '',
    app: '',
    url_app: '',
    url_youtube: '',
    url_instagram: '',
    url_other: ''
  });

  // Password change fields
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (!creator) {
      navigate('/login');
      return;
    }
    
    fetchProfile();
    fetchCountries();
  }, [creator, navigate]);

  const fetchProfile = async () => {
    try {
      const response = await getProfile();
      const creatorData = response.data;
      
      // creatorData should have a 'creator' or the Creator object directly
      const creatorProfile = creatorData.creator || creatorData;
      setProfile(creatorProfile);
      
      // Populate form with profile data
      if (creatorProfile) {
        setFormData({
          name: creatorProfile.name || '',
          alias: creatorProfile.alias || '',
          phone: creatorProfile.phone || '',
          avatar_url: creatorProfile.avatar_url || '',
          country: creatorProfile.country || '',
          description: creatorProfile.description || '',
          app: creatorProfile.app || '',
          url_app: creatorProfile.url_app || '',
          url_youtube: creatorProfile.url_youtube || '',
          url_instagram: creatorProfile.url_instagram || '',
          url_other: creatorProfile.url_other || ''
        });
        // Set preview if avatar exists
        if (creatorProfile.avatar_url) {
          setAvatarPreview(`https://x-perienc.es/${creatorProfile.avatar_url}`);
        }
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      setError(t('profile.error.loadProfile'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await getCountries();
      setCountries(response.data);
    } catch (err) {
      console.error('Failed to fetch countries:', err);
    }
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setAvatarPreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    // Save for upload when Save Changes is clicked
    setSelectedAvatarFile(file);
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setInfoMessage('');
    setSaving(true);

    try {
      let finalFormData = { ...formData };

      // Upload avatar if a new one was selected
      if (selectedAvatarFile) {
        setUploadingAvatar(true);
        try {
          const avatarResponse = await uploadAvatar(selectedAvatarFile);
          finalFormData.avatar_url = avatarResponse.data.avatarPath;
          setSelectedAvatarFile(null); // Clear selected file after successful upload
        } catch (err) {
          console.error('Avatar upload error:', err);
          setError(`✗ ${t('profile.error.avatarUpload')}`);
          setSaving(false);
          setUploadingAvatar(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        setUploadingAvatar(false);
      }

      // Update profile
      const response = await updateProfile(finalFormData);
      setProfile(response.data.creator);
      
      // Sync form with potentially updated data from backend
      setFormData(prev => ({
        ...prev,
        avatar_url: response.data.creator.avatar_url || finalFormData.avatar_url || ''
      }));
      
      let successMessage = '';
      
      // Handle password change if fields are filled
      if (passwordData.current_password || passwordData.new_password || passwordData.confirm_password) {
        // Validate all password fields are filled
        if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
          setError(`✗ ${t('profile.error.fillAllPasswordFields')}`);
          setSaving(false);
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
          return;
        }
        
        // Validate new passwords match
        if (passwordData.new_password !== passwordData.confirm_password) {
          setError(`✗ ${t('profile.error.passwordsMismatch')}`);
          setSaving(false);
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
          return;
        }
        
        // Validate minimum length
        if (passwordData.new_password.length < 6) {
          setError(`✗ ${t('profile.error.passwordMin')}`);
          setSaving(false);
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
          return;
        }
        
        try {
          await changePassword(passwordData.current_password, passwordData.new_password);
          // Clear password fields
          setPasswordData({
            current_password: '',
            new_password: '',
            confirm_password: ''
          });
          successMessage = `✓ ${t('profile.success.profileAndPasswordUpdated')}`;
        } catch (err: any) {
          const errorMessage = t('profile.error.changePassword');
          setError(`✗ ${t('profile.error.profileUpdatedPasswordFailed')}: ${errorMessage}`);
          setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }, 100);
          setSaving(false);
          return;
        }
      } else {
        // Show detailed success message for profile only
        if (response.data.isProfileComplete) {
          successMessage = `✓ ${t('profile.success.profileUpdatedComplete')}`;
        } else {
          successMessage = `✓ ${t('profile.success.profileUpdatedIncomplete')}`;
        }
      }
      
      setSuccess(successMessage);
      
      // Scroll to top after message is set
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      
      // Refresh profile status in AuthContext
      await refreshProfileStatus();
      
      // If profile is now complete and user was redirected here, send them back to dashboard
      if (response.data.isProfileComplete && redirectMessage) {
        setTimeout(() => {
          navigate('/');
        }, 2500);
      } else {
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (err: any) {
      const errorMessage = t('profile.error.updateProfile');
      setError(`✗ ${errorMessage}`);
      
      // Scroll to top after error message is set
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      
      console.error('Profile update error:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <Navbar />
        <div className="profile-container">
          <div className="loading">{t('profile.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <Navbar />
      
      <div className="profile-container">
        <div className="profile-header">
          <h1 className="profile-title">{t('profile.title')}</h1>
          <p className="profile-subtitle">{t('profile.subtitle')}</p>
          <p className="profile-note">
            <strong>{t('profile.requiredFieldsLabel')}</strong> {t('profile.requiredFieldsText')}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="profile-form">
          {error && <div className="form-error">{error}</div>}
          {success && <div className="form-success">{success}</div>}
          {infoMessage && <div className="form-info">{infoMessage}</div>}

          {/* Account Information */}
          <div className="form-section">
            <h2 className="section-title">{t('profile.section.accountInfo')}</h2>
            
            <div className="form-group">
              <label className="form-label">{t('profile.field.usernameAlias')}</label>
              <Input
                type="text"
                placeholder={t('profile.placeholder.username')}
                value={formData.alias}
                onChange={(e) => handleInputChange('alias', e.target.value)}
              />
              <span className="field-note">{t('profile.note.publicUsername')}</span>
            </div>

            <div className="form-group">
              <label className="form-label">{t('common.email')}</label>
              <input
                type="email"
                value={profile?.email || ''}
                disabled
                className="form-input-disabled"
              />
              <span className="field-note">{t('profile.note.emailImmutable')}</span>
            </div>
          </div>

          {/* Change Password */}
          <div className="form-section">
            <h2 className="section-title">{t('profile.section.changePassword')}</h2>
            
            <div className="form-group">
              <label className="form-label">{t('profile.field.currentPassword')}</label>
              <Input
                type="password"
                placeholder={t('profile.placeholder.currentPassword')}
                value={passwordData.current_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.field.newPassword')}</label>
              <Input
                type="password"
                placeholder={t('profile.placeholder.newPassword')}
                value={passwordData.new_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
              />
              <span className="field-note">{t('profile.note.passwordMin')}</span>
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.field.confirmNewPassword')}</label>
              <Input
                type="password"
                placeholder={t('profile.placeholder.confirmNewPassword')}
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
              />
            </div>
          </div>

          {/* Personal Information */}
          <div className="form-section">
            <h2 className="section-title">{t('profile.section.personalInfo')}</h2>
            
            <div className="form-group">
              <label className="form-label">{t('profile.field.fullNameRequired')}</label>
              <Input
                type="text"
                placeholder={t('profile.placeholder.fullName')}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
              <span className="field-note">{t('profile.note.requiredForDashboard')}</span>
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.field.phone')} <span className="optional-label">({t('profile.optional')})</span></label>
              <Input
                type="tel"
                placeholder={t('profile.placeholder.phone')}
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.field.countryRequired')}</label>
              <Select
                options={countries.map(c => ({
                  value: c.code,
                  label: `${c.code} - ${c.name} (${c.full_name})`
                }))}
                value={formData.country ? 
                  countries.find(c => c.code === formData.country) ? 
                    {
                      value: formData.country,
                      label: `${formData.country} - ${countries.find(c => c.code === formData.country)?.name} (${countries.find(c => c.code === formData.country)?.full_name})`
                    } 
                    : null
                  : null
                }
                onChange={(option) => handleInputChange('country', option?.value || '')}
                isClearable
                isSearchable
                placeholder={t('profile.placeholder.searchCountries')}
                className="react-select-container"
                classNamePrefix="react-select"
              />
              <span className="field-note">{t('profile.note.countryRequired')}</span>
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.field.avatarRequired')}</label>
              <div className="avatar-upload-section">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  disabled={uploadingAvatar}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingAvatar}
                  className="avatar-upload-button"
                >
                  {uploadingAvatar ? t('profile.uploading') : t('profile.chooseAvatarImage')}
                </button>
                <span className="field-note">
                  {uploadingAvatar ? t('profile.uploadingAvatar') : t('profile.note.avatarRequired')}
                </span>
              </div>
              {avatarPreview && (
                <div className="avatar-preview">
                  <img src={avatarPreview} alt={t('profile.avatarPreviewAlt')} />
                  <p className="preview-label">{t('profile.preview')}</p>
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.field.biographyRequired')}</label>
              <div className="clearable-field clearable-field-textarea">
                <textarea
                  placeholder={t('profile.placeholder.biography')}
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  maxLength={1024}
                  rows={10}
                  className="form-textarea clearable-textarea"
                  required
                />
                {formData.description && (
                  <button
                    type="button"
                    className="clearable-field-button"
                    onClick={() => handleInputChange('description', '')}
                    aria-label={t('common.clearSearch')}
                    title={t('common.clearSearch')}
                  >
                    x
                  </button>
                )}
              </div>
              <span className="field-note">{t('profile.note.biographyRequiredPrefix')} {formData.description.length}/1024 {t('profile.note.characters')}</span>
            </div>
          </div>

          {/* App & Social Media */}
          <div className="form-section">
            <h2 className="section-title">{t('profile.section.appSocial')} <span className="optional-section">({t('profile.allOptional')})</span></h2>
            
            <div className="form-group">
              <label className="form-label">{t('profile.field.appName')}</label>
              <Input
                type="text"
                placeholder={t('profile.placeholder.appName')}
                value={formData.app}
                onChange={(e) => handleInputChange('app', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.field.appUrl')}</label>
              <div className="url-input-wrapper">
                <div className="clearable-field" style={{ flex: 1 }}>
                  <input
                    type="url"
                    className="input clearable-field-control"
                    placeholder={t('profile.placeholder.appUrl')}
                    value={formData.url_app}
                    onChange={(e) => handleInputChange('url_app', e.target.value)}
                  />
                  {formData.url_app && (
                    <button
                      type="button"
                      className="clearable-field-button"
                      onClick={() => handleInputChange('url_app', '')}
                      aria-label={t('common.clearSearch')}
                      title={t('common.clearSearch')}
                    >
                      x
                    </button>
                  )}
                </div>
                {formData.url_app && (
                  <button
                    type="button"
                    className="url-view-button"
                    onClick={() => window.open(formData.url_app, '_blank')}
                    title={t('profile.openInNewTab')}
                  >
                    {t('profile.view')}
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.field.youtubeUrl')}</label>
              <div className="url-input-wrapper">
                <div className="clearable-field" style={{ flex: 1 }}>
                  <input
                    type="url"
                    className="input clearable-field-control"
                    placeholder={t('profile.placeholder.youtubeUrl')}
                    value={formData.url_youtube}
                    onChange={(e) => handleInputChange('url_youtube', e.target.value)}
                  />
                  {formData.url_youtube && (
                    <button
                      type="button"
                      className="clearable-field-button"
                      onClick={() => handleInputChange('url_youtube', '')}
                      aria-label={t('common.clearSearch')}
                      title={t('common.clearSearch')}
                    >
                      x
                    </button>
                  )}
                </div>
                {formData.url_youtube && (
                  <button
                    type="button"
                    className="url-view-button"
                    onClick={() => window.open(formData.url_youtube, '_blank')}
                    title={t('profile.openInNewTab')}
                  >
                    {t('profile.view')}
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.field.instagramUrl')}</label>
              <div className="url-input-wrapper">
                <div className="clearable-field" style={{ flex: 1 }}>
                  <input
                    type="url"
                    className="input clearable-field-control"
                    placeholder={t('profile.placeholder.instagramUrl')}
                    value={formData.url_instagram}
                    onChange={(e) => handleInputChange('url_instagram', e.target.value)}
                  />
                  {formData.url_instagram && (
                    <button
                      type="button"
                      className="clearable-field-button"
                      onClick={() => handleInputChange('url_instagram', '')}
                      aria-label={t('common.clearSearch')}
                      title={t('common.clearSearch')}
                    >
                      x
                    </button>
                  )}
                </div>
                {formData.url_instagram && (
                  <button
                    type="button"
                    className="url-view-button"
                    onClick={() => window.open(formData.url_instagram, '_blank')}
                    title={t('profile.openInNewTab')}
                  >
                    {t('profile.view')}
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{t('profile.field.otherUrl')}</label>
              <div className="url-input-wrapper">
                <div className="clearable-field" style={{ flex: 1 }}>
                  <input
                    type="url"
                    className="input clearable-field-control"
                    placeholder={t('profile.placeholder.otherUrl')}
                    value={formData.url_other}
                    onChange={(e) => handleInputChange('url_other', e.target.value)}
                  />
                  {formData.url_other && (
                    <button
                      type="button"
                      className="clearable-field-button"
                      onClick={() => handleInputChange('url_other', '')}
                      aria-label={t('common.clearSearch')}
                      title={t('common.clearSearch')}
                    >
                      x
                    </button>
                  )}
                </div>
                {formData.url_other && (
                  <button
                    type="button"
                    className="url-view-button"
                    onClick={() => window.open(formData.url_other, '_blank')}
                    title={t('profile.openInNewTab')}
                  >
                    {t('profile.view')}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Creator Stats (Read-only) */}
          <div className="form-section">
            <h2 className="section-title">{t('profile.section.creatorStats')}</h2>
            
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-label">{t('profile.stats.rating')}</span>
                <span className="stat-value">{profile?.rating || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{t('profile.stats.credits')}</span>
                <span className="stat-value">{profile?.credits || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{t('profile.stats.views')}</span>
                <span className="stat-value">{profile?.views || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">{t('profile.stats.votes')}</span>
                <span className="stat-value">{profile?.votes || 0}</span>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <Button 
              type="submit" 
              variant="primary" 
              fullWidth 
              loading={saving}
              disabled={saving}
            >
              {saving ? t('profile.saving') : t('profile.saveChanges')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
